import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface RailItem {
  id: string;
  /** Estonian label (docs/UI_STANDARDS.md — UI labels stay Estonian). */
  label: string;
  /** Single-glyph/short icon stand-in until the icon set lands. */
  icon: ReactNode;
  active?: boolean;
}

export interface IconRailProps {
  items: RailItem[];
  /** Fired with the item id when a nav entry is chosen (App drives the route). */
  onSelect?: (id: string) => void;
}

/**
 * Collapsible left icon rail (plan §3.2, docs/UI_STANDARDS.md §3). Minimal
 * persistent chrome so the calendar keeps maximum space: collapsed shows icons
 * only; expanded reveals the Estonian labels. Nothing else here is persistent.
 */
export function IconRail({ items, onSelect }: IconRailProps): ReactNode {
  const [expanded, setExpanded] = useState(false);

  return (
    <nav
      aria-label="Peamenüü"
      data-expanded={expanded}
      className={cn(
        'flex h-full flex-col gap-1 border-r border-border bg-sidebar p-2 transition-all',
        expanded ? 'w-48' : 'w-14',
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={expanded ? 'Ahenda külgriba' : 'Laienda külgriba'}
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded ? '«' : '»'}
      </Button>
      {items.map((item) => (
        <Button
          key={item.id}
          type="button"
          variant={item.active ? 'default' : 'ghost'}
          className={cn('justify-start', expanded ? 'w-full' : 'w-10 px-0 justify-center')}
          title={item.label}
          aria-current={item.active ? 'page' : undefined}
          onClick={() => onSelect?.(item.id)}
        >
          <span aria-hidden="true">{item.icon}</span>
          <span className={cn(expanded ? 'inline' : 'sr-only')}>{item.label}</span>
        </Button>
      ))}
      {/* Build stamp (bottom-left): compare the Replit preview against
          production at a glance. `*` = uncommitted local changes; `· dev`
          = stamped at dev-server start (a restart refreshes it). */}
      <div
        className="mt-auto select-all whitespace-normal pt-2 text-center text-[9px] leading-tight tabular-nums text-muted-foreground/60"
        title="Versioon 2.N — N on mitmes commit see build on. * = commitmata muudatused; dev-tempel uueneb serveri restardiga."
      >
        {__APP_VERSION__}
      </div>
    </nav>
  );
}
